import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Prohibit } from '@phosphor-icons/react';

/**
 * BlockButton — toggles a block/unblock for a target user.
 * Pass `targetUserId` (required) and optional `targetUsername` for the toast.
 * Hides the subject's content/scenes/comments wherever the backend respects
 * the `blocks` collection.
 */
const BlockButton = ({ targetUserId, targetUsername, className = '' }) => {
  const [blocked, setBlocked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get('/blocks');
        if (!alive) return;
        const yes = (data.blocks || []).some((b) => b.blocked_id === targetUserId);
        setBlocked(yes);
      } catch { /* silent — endpoint is auth-only */ }
      finally { if (alive) setLoaded(true); }
    })();
    return () => { alive = false; };
  }, [targetUserId]);

  const toggle = async () => {
    if (!targetUserId) return;
    setBusy(true);
    try {
      if (blocked) {
        await api.delete(`/blocks/${targetUserId}`);
        setBlocked(false);
        toast.success(`@${targetUsername || 'user'} unblocked`);
      } else {
        await api.post(`/blocks/${targetUserId}`);
        setBlocked(true);
        toast.success(`@${targetUsername || 'user'} blocked`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'could not update');
    } finally { setBusy(false); }
  };

  if (!loaded || !targetUserId) return null;

  return (
    <button
      onClick={toggle}
      disabled={busy}
      data-testid={blocked ? 'unblock-btn' : 'block-btn'}
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-white/45 hover:text-bx-magenta disabled:opacity-50 ${className}`}
    >
      <Prohibit size={11} weight="duotone" />
      {blocked ? 'unblock' : 'block'}
    </button>
  );
};

export default BlockButton;
